import { ArrowUpRight } from "lucide-react";
import { Section } from "./Section";
import { LIVE_URL } from "@/lib/api";

const METRICS = [
  ["CAGR (illustrative)", "34.8%", "text-[var(--tn-green)]"],
  ["Max drawdown", "-12.4%", "text-[var(--tn-red)]"],
  ["Sharpe ratio", "1.86", ""],
  ["Win rate", "61.2%", ""],
];

const ROWS = [
  ["Opening Range Breakout", "NIFTY 50 options", "2015–2024", "34.8%", "-12.4%", "Backtested"],
  ["Weekly Expiry Straddle", "BANKNIFTY (weekly)", "2018–2024", "27.1%", "-18.6%", "Paper traded"],
  ["Trend Following", "NIFTY futures", "2016–2024", "21.4%", "-9.8%", "Paper traded"],
  ["Dividend Compounder Basket", "Large-cap equity", "2012–2024", "17.2%", "-15.3%", "Backtested"],
];

export const Performance = () => (
  <Section id="performance" testId="performance-section" eyebrow="Live performance" title="Track records you can open, not screenshots you have to trust"
    intro="Once the platform goes live, every strategy carries its backtest, its paper-trading history and its real fills. Verified live results are published on our dedicated performance dashboard at live.tradingnifty.in.">
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5">
      <div className="tn-card tn-reveal p-6" data-testid="performance-sample-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display font-medium">Sample AI strategy · NIFTY ORB</h3>
            <p className="font-mono text-[11px] text-[var(--tn-muted)] mt-1">Backtest window 2015–2024 · costs &amp; slippage applied</p>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-wider text-[#facc15] border border-[rgba(250,204,21,0.35)] rounded-full px-2.5 py-1">Illustrative</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--tn-line)] rounded-lg overflow-hidden mt-6">
          {METRICS.map(([k, v, c]) => (
            <div key={k} className="bg-[var(--tn-card)] p-4">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--tn-muted)]">{k}</div>
              <div className={`font-mono text-2xl mt-2 ${c}`}>{v}</div>
            </div>
          ))}
        </div>
      </div>
      <a href={LIVE_URL} target="_blank" rel="noreferrer" aria-label="Live performance dashboard" data-testid="performance-live-dashboard-link"
        className="tn-card tn-reveal p-6 flex flex-col justify-between group">
        <div>
          <h3 className="font-display font-medium text-lg">Open the live dashboard</h3>
          <p className="text-sm text-[var(--tn-muted)] mt-3 leading-relaxed">
            Real strategies, real fills, real drawdowns — updated as the market moves. The dashboard is where verified performance is published, including strategy-level equity curves and month-by-month returns.
          </p>
        </div>
        <span className="font-mono text-sm text-[var(--tn-cyan)] mt-6 flex items-center gap-2">
          live.tradingnifty.in <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </a>
    </div>
    <div className="tn-reveal mt-5 rounded-xl border border-[var(--tn-line)] overflow-x-auto" data-testid="performance-table">
      <table className="w-full min-w-[680px] text-sm">
        <thead className="bg-[var(--tn-bg-2)]">
          <tr>{["Strategy", "Market", "Period", "CAGR", "Max DD", "Status"].map((h) => (
            <th key={h} className="text-left font-mono text-[10px] uppercase tracking-wider text-[var(--tn-muted)] font-normal px-5 py-3">{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {ROWS.map(([s, m, p, c, dd, st]) => (
            <tr key={s} className="border-t border-[var(--tn-line)] hover:bg-[rgba(34,211,238,0.03)] transition-colors">
              <td className="px-5 py-4 font-medium">{s}</td>
              <td className="px-5 py-4 text-[var(--tn-muted)]">{m}</td>
              <td className="px-5 py-4 font-mono text-[var(--tn-muted)]">{p}</td>
              <td className="px-5 py-4 font-mono text-[var(--tn-green)]">{c}</td>
              <td className="px-5 py-4 font-mono text-[var(--tn-red)]">{dd}</td>
              <td className="px-5 py-4">
                <span className={`font-mono text-[10px] uppercase px-2 py-1 rounded border ${st === "Backtested" ? "text-[var(--tn-cyan)] border-[rgba(34,211,238,0.35)]" : "text-[var(--tn-green)] border-[rgba(163,230,53,0.35)]"}`}>{st}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="text-xs text-[var(--tn-muted)] mt-5">Illustrative examples of the metrics each strategy publishes. These figures are examples, not verified returns or a promise of future performance.</p>
    <p className="text-xs text-[var(--tn-muted)] mt-2">Past performance — backtested or live — does not guarantee future results. Derivatives trading carries a high risk of loss.</p>
  </Section>
);
